import { useState } from 'react';
import { ChevronLeft, CheckCircle } from 'lucide-react';
import { contact } from "../../ui/media"
import { useFillContactUsForm } from "../../../hooks/useCommon"
import { useNavigate } from "react-router-dom"
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';
import toast from 'react-hot-toast';

const ContactUsForm = () => {
    const { isDarkMode } = useStore(themeStore)
    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [message, setMessage] = useState("")
    const [showSuccess, setShowSuccess] = useState(false);
    const contactFillMutation = useFillContactUsForm()
    const navigate = useNavigate()

    const handleSubmit = async () => {
        if (name == "" || email == "" || phoneNumber == "" || message == "") {
            toast.error("Please Fill the complete form to continue.")
            return false
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            toast.error('Please enter a valid email address');
            return
        }
        contactFillMutation.mutate({
            name,
            email,
            phone: phoneNumber,
            message
        }, {
            onSuccess: () => {
                setName("")
                setEmail("")
                setPhoneNumber("")
                setMessage("")
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Unable to Fill Contact Form"
                toast.error(message)
            }
        })
    };

    const handleBackClick = () => {
        navigate(-1)
    };

    return (
        <div className={`min-h-screen ${isDarkMode?"bg-gray-800": "bg-white"}`}>
            {/* Success Message Overlay */}
            {showSuccess && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`${isDarkMode?"bg-gray-700 text-white":"bg-white text-gray-700"} rounded-2xl p-8 shadow-2xl max-w-md mx-4`}>
                        <div className="text-center">
                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className={`text-2xl font-bold ${isDarkMode?"text-white":"text-gray-700"} mb-2`}>Message Sent!</h3>
                            <p className={`${isDarkMode?"text-white":"text-gray-700"}`}>
                                Thank you for contacting us. We'll get back to you soon!
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <button
                    onClick={handleBackClick}
                    className={`${isDarkMode? "bg-gray-800": "bg-white" } mb-8 p-3 border-2 border-blue-500 rounded-lg transition-colors shadow-sm`}>
                    <ChevronLeft className="w-6 h-6 text-blue-600" />
                </button>
                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div className="order-2 lg:order-1">
                        <div className="relative">
                            <img
                                src={contact}
                                alt="Hand holding phone"
                                className="w-full h-auto rounded-3xl shadow-2xl"
                            />
                        </div>
                    </div>
                    {/* Right Side - Contact Form */}
                    <div className="order-1 lg:order-2">
                        <div className="max-w-md mx-auto lg:mx-0">
                            <h1 className={`text-4xl font-bold ${isDarkMode?"text-white":"text-gray-700"} mb-4`}>Contact Us</h1>
                            <p className={`${isDarkMode?"text-white":"text-gray-700"}`}>We're here to help!</p>
                            <p className={`${isDarkMode?"text-white":"text-gray-700"} mb-8`}>We look forward to hearing from you.</p>
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="name" className={`block text-sm font-medium ${isDarkMode?"text-white":"text-gray-700"} mb-2`}>Name*</label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className={`${isDarkMode?"bg-gray-700 text-white":"bg-white text-gray-700"} w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300`}
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className={`block text-sm font-medium ${isDarkMode?"text-white":"text-gray-700"} mb-2`}>
                                        Email*
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={`${isDarkMode?"bg-gray-700 text-white":"bg-white text-gray-700"} w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300`}
                                        placeholder="Enter your email"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="phone_number" className={`block text-sm font-medium ${isDarkMode?"text-white":"text-gray-700"} mb-2`}>
                                        Phone Number*
                                    </label>
                                    <input
                                        type="text"
                                        id="phone_number"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        className={`${isDarkMode?"bg-gray-700 text-white":"bg-white text-gray-700"} w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300`}
                                        placeholder="Enter your phone number"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="message" className={`block text-sm font-medium ${isDarkMode?"text-white":"text-gray-700"} mb-2`}>Message*</label>
                                    <textarea
                                        id="message"
                                        rows={5}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        className={`${isDarkMode?"bg-gray-700 text-white":"bg-white text-gray-700"} w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none border-gray-300`}
                                        placeholder="Enter your message"
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubmit}
                                    disabled={contactFillMutation.isPending}
                                    className={`w-[200px] py-3 px-6 rounded-full text-white transition-all duration-200 ${contactFillMutation.isPending
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:scale-105 active:scale-95'
                                        }`}>
                                    {contactFillMutation.isPending ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Sending...</span>
                                        </div>
                                    ) : (
                                        'Send'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUsForm;